'use client';

import React, { useState, useEffect } from 'react';
import Breadcrumb from '../../components/Breadcrumb';
import RecordAgentPanel from '../../components/crm/RecordAgentPanel';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { DealFinancePanel } from '../../components/crm/billing/CustomerFinancePanel';
import {
  ArrowLeft, Edit2, CheckCircle, XCircle, AlertCircle, TrendingUp, Calendar, Clock, FileText, Activity, Wallet,
  ShieldCheck, ChevronRight, Hash, User, Building2, MoreVertical, ListChecks, Share2, UserCog, History
} from 'lucide-react';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { formatDate } from '../../utils/formatDate';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import ApplyTemplateModal from '../../components/crm/ApplyTemplateModal';
import ShareModal from '../../components/crm/ShareModal';
import TransferOwnershipModal from '../../components/crm/TransferOwnershipModal';
import RelatedFiles from '../../components/crm/RelatedFiles';
import DealLineItems from '../../components/crm/DealLineItems';
import AuditHistory from '../../components/crm/AuditHistory';
import PathBar from '../../components/crm/PathBar';
import DealOutcomeModal from '../../components/crm/DealOutcomeModal';
import GenerateInvoiceModal from '../../components/crm/GenerateInvoiceModal';
import BillingSliceModal from '../../components/crm/billing/BillingSliceModal';
import FormActivityPanel from '../../components/crm/FormActivityPanel';
import Tabs from '../../components/ui/Tabs';
import { PageLoader } from '../../components/crm/ui';

const TABS = [
  { id: 'overview', label: 'Details', icon: FileText },
  { id: 'activities', label: 'Activity History', icon: Activity },
  { id: 'tasks', label: 'Related Tasks', icon: Clock },
  { id: 'files', label: 'Files', icon: FileText },
  { id: 'formActivity', label: 'Form Activity', icon: FileText },
  { id: 'invoices', label: 'Invoices', icon: Wallet },
  { id: 'finance', label: 'Finance', icon: Wallet },
  { id: 'history', label: 'History', icon: History },
];

const STAGES = {
  new:          { label: 'New',          badge: 'sf-badge-neutral' },
  qualified:    { label: 'Qualified',    badge: 'sf-badge-neutral' },
  proposal:     { label: 'Proposal',     badge: 'sf-badge-warning' },
  negotiation:  { label: 'Negotiation',  badge: 'sf-badge-warning' },
  closed_won:   { label: 'Closed Won',   badge: 'sf-badge-success' },
  closed_lost:  { label: 'Closed Lost',  badge: 'sf-badge-error' },
};
const STAGE_ORDER = ['new', 'qualified', 'proposal', 'negotiation', 'closed_won'];

function fmtCurrency(value, currency) {
  return formatCurrency(value, currency);
}

export default function DealDetailPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const companyCurrency = useCompanyCurrency();

  const [deal, setDeal] = useState(null);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [dealBilling, setDealBilling] = useState(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState(null);
  const [outcomeModal, setOutcomeModal] = useState({ open: false, mode: null });

  useEffect(() => { fetchDealData(); }, [dealId]);

  const fetchDealData = async () => {
    try {
      setLoading(true);
      const [dealRes, invoicesRes] = await Promise.all([
        api.get(`/crm/deals/${dealId}`),
        api.get('/crm/invoices', { params: { dealId } }),
      ]);
      setDeal(dealRes.data.deal);
      setActivities(dealRes.data.activities || []);
      setTasks(dealRes.data.tasks || []);
      setFormData(dealRes.data.deal);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not load deal record.';
      setError(msg);
      alertService.error('Error', msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      const response = await api.put(`/crm/deals/${dealId}`, formData);
      setDeal(response.data.deal);
      setIsEditing(false);
      alertService.success('Success', 'Deal updated');
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to update deal');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleMarkWon = () => setOutcomeModal({ open: true, mode: 'won' });
  const handleMarkLost = () => setOutcomeModal({ open: true, mode: 'lost' });
  const closeOutcomeModal = () => setOutcomeModal({ open: false, mode: null });

  const handleOutcomeConfirm = async ({ reasonCategory, note, competitorName }) => {
    const { mode } = outcomeModal;
    try {
      if (mode === 'won') {
        const response = await api.post(`/crm/deals/${dealId}/won`, { winReason: reasonCategory, note });
        setDeal(response.data.deal);
        closeOutcomeModal();
        // Won is the moment to bill: offer the invoice straight away.
        const invoiceNow = await alertService.confirm(
          'Deal won — create the invoice now?',
          response.data.promotedToCustomer ? 'The contact/account was promoted to Customer.' : 'You can also do this later from Create Invoice.',
          'success', 'Create invoice'
        );
        if (invoiceNow) handleCreateInvoice();
        return;
      } else {
        const response = await api.post(`/crm/deals/${dealId}/lost`, { lostReason: reasonCategory, note, competitorName });
        setDeal(response.data.deal);
        alertService.success('Success', 'Deal marked as lost');
      }
      closeOutcomeModal();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to update deal');
    }
  };

  const handleLineItemsChange = async (lineItems) => {
    try {
      const response = await api.put(`/crm/deals/${dealId}`, { lineItems });
      setDeal(response.data.deal);
      setFormData(response.data.deal);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to update line items');
    }
  };

  const handleCreateInvoice = async () => {
    // A deal with no Account/Contact linked can't generate an invoice yet
    // (createFromDeal requires at least one) — send those cases through the
    // modal to pick/create one instead of failing with a raw 400.
    if (!deal.accountId && !deal.crmContactId) {
      setShowGenerateInvoiceModal(true);
      return;
    }
    try {
      const { data } = await api.get(`/crm/invoices/deal-billing/${dealId}`);
      setDealBilling(data);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Could not load billing details');
    }
  };
  // Arriving from the pipeline board right after winning: open billing once.
  useEffect(() => {
    if (deal && searchParams.get('createInvoice') === '1') {
      setSearchParams({}, { replace: true });
      handleCreateInvoice();
    }
  }, [deal]); // eslint-disable-line react-hooks/exhaustive-deps


  const submitDealInvoice = async (billing) => {
    setCreatingInvoice(true);
    try {
      const response = await api.post(`/crm/invoices/from-deal/${dealId}`, { billing });
      alertService.success('Success', `Invoice ${response.data.invoice.invoiceNumber} created`);
      setInvoices((prev) => [response.data.invoice, ...prev]);
      setDealBilling(null);
      setActiveTab('invoices');
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleStageClick = async (stage) => {
    if (stage === deal.stage || saveLoading) return;
    try {
      setSaveLoading(true);
      const response = await api.put(`/crm/deals/${dealId}/stage`, { stage });
      setDeal(response.data.deal);
      alertService.success('Success', `Deal moved to ${STAGES[stage].label}`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to move deal stage');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  if (!deal) {
    return (
      <div className="p-12 text-center space-y-4">
        <div className="w-16 h-16 bg-[#F4F6F9] rounded-full flex items-center justify-center mx-auto text-[#706E6B]">
          <AlertCircle size={32} />
        </div>
        <h2 className="text-xl font-bold text-[#080707]">Deal Not Found</h2>
        <button onClick={() => navigate('/dashboard/crm/deals')} className="text-[#0176D3] font-bold text-sm hover:underline flex items-center justify-center gap-2 mx-auto">
          <ArrowLeft size={16} /> Return to Deals
        </button>
      </div>
    );
  }

  const stageInfo = STAGES[deal.stage] || STAGES.new;
  const currentIndex = STAGE_ORDER.indexOf(deal.stage);
  const isClosed = deal.status === 'won' || deal.status === 'lost';

  return (
    <div className="py-4 sm:py-6 px-0 sm:px-4 space-y-6 max-w-[100rem] mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500">

      {/* Navigation Breadcrumbs */}
      <Breadcrumb className="mb-2" items={[{ label: 'Deals', to: '/dashboard/crm/deals' }, { label: deal.title }]} />
      <RecordAgentPanel entityType="deal" entityId={dealId} />

      {/* Profile Header */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-[#0176D3] flex items-center justify-center text-white">
              <TrendingUp size={20} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <input
                    className="text-lg sm:text-xl font-bold text-[#2E2E2E] border-b border-dashed border-[#C9C9C9] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  />
                ) : (
                  <h1 className="text-lg sm:text-xl font-bold text-[#2E2E2E]">{deal.title}</h1>
                )}
                <span className={cn('sf-badge', stageInfo.badge)}>{stageInfo.label}</span>
              </div>
              <div className="flex items-center gap-4 text-[12px] text-[#706E6B] font-medium">
                <span className="flex items-center gap-1.5"><Wallet size={12} /> {fmtCurrency(deal.value, deal.currency || companyCurrency)}</span>
                {deal.leadId?.name && (
                  <span className="flex items-center gap-1.5"><User size={12} /> {deal.leadId.name}</span>
                )}
                {deal.contactId?.name && (
                  <span className="flex items-center gap-1.5"><Building2 size={12} /> {deal.contactId.name}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button onClick={() => setIsEditing(false)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F3F3] whitespace-nowrap shrink-0">Cancel</button>
                <button onClick={handleSave} disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50 whitespace-nowrap shrink-0">
                  {saveLoading ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setIsEditing(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Edit2 size={14} /> Edit
                </button>
                <button onClick={() => setShowApplyTemplate(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <ListChecks size={14} /> Apply Template
                </button>
                <button onClick={() => setShowShareModal(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={() => setShowTransferModal(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <UserCog size={14} /> Transfer
                </button>
                {!isClosed && (
                  <>
                    <button onClick={handleMarkWon} className="h-9 px-4 bg-[#2E844A] text-white rounded text-[13px] font-semibold hover:bg-[#256b3c] flex items-center gap-2 whitespace-nowrap shrink-0">
                      <CheckCircle size={14} /> Mark Won
                    </button>
                    <button onClick={handleMarkLost} className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#BA0517] rounded text-[13px] font-semibold hover:bg-[#FDE7E9] flex items-center gap-2 whitespace-nowrap shrink-0">
                      <XCircle size={14} /> Mark Lost
                    </button>
                  </>
                )}
                {deal.status === 'won' && (
                  <button onClick={handleCreateInvoice} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                    <Wallet size={14} /> Create Invoice
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Pipeline Progression */}
        {!isClosed && (
          <div className="mt-8 pt-6 border-t border-[#D8DDE6] space-y-3">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Pipeline Progression</p>
            <PathBar
              stages={STAGE_ORDER.map(s => ({ id: s, label: STAGES[s].label }))}
              currentIndex={currentIndex}
              onStageClick={handleStageClick}
              disabled={saveLoading}
            />
          </div>
        )}

        {/* Highlight Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Value</p>
            <p className="text-[13px] font-medium text-[#080707] tabular-nums">{fmtCurrency(deal.value, deal.currency || companyCurrency)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Confidence</p>
            <p className="text-[13px] font-medium text-[#080707] tabular-nums">{deal.probability || 0}%</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Expected Close</p>
            <p className="text-[13px] font-medium text-[#080707]">{deal.expectedCloseDate ? formatDate(deal.expectedCloseDate) : 'Not set'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Assigned To</p>
            <p className="text-[13px] font-medium text-[#080707]">{deal.assignedTo || 'Unassigned'}</p>
          </div>
        </div>
      </div>

      {/* Tabs Control */}
      <Tabs tabs={TABS.map((t) => ({ key: t.id, label: t.label, icon: t.icon }))} value={activeTab} onChange={setActiveTab} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          {activeTab === 'overview' && (
            <div className="sf-card p-8 bg-white space-y-8 animate-in fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Deal Value{deal.lineItems?.length > 0 && ' (from line items)'}</label>
                  {isEditing && !(deal.lineItems?.length > 0) ? (
                    <input type="number" min="0" step="0.01" className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.value} onChange={e => setFormData({ ...formData, value: Number(e.target.value) })} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1 tabular-nums">{fmtCurrency(deal.value, deal.currency || companyCurrency)}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Confidence (%)</label>
                  {isEditing ? (
                    <input type="number" min="0" max="100" className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.probability} onChange={e => setFormData({ ...formData, probability: Number(e.target.value) })} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1 tabular-nums">{deal.probability || 0}%</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Expected Close Date</label>
                  {isEditing ? (
                    <input type="date" className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.expectedCloseDate ? formData.expectedCloseDate.slice(0, 10) : ''} onChange={e => setFormData({ ...formData, expectedCloseDate: e.target.value })} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{deal.expectedCloseDate ? formatDate(deal.expectedCloseDate) : 'Not set'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Assigned To</label>
                  {isEditing ? (
                    <input className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.assignedTo || ''} onChange={e => setFormData({ ...formData, assignedTo: e.target.value })} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{deal.assignedTo || 'Unassigned'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Visibility</label>
                  {isEditing ? (
                    <select className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" value={formData.visibility || 'team'} onChange={e => setFormData({ ...formData, visibility: e.target.value })}>
                      <option value="team">Team — everyone can see this</option>
                      <option value="private">Private — only owner and shared users</option>
                    </select>
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{deal.visibility === 'private' ? 'Private' : 'Team'}</p>
                  )}
                </div>
              </div>

              <DealLineItems
                lineItems={deal.lineItems}
                currency={deal.currency || companyCurrency}
                onChange={handleLineItemsChange}
              />

              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B]">Notes</label>
                {isEditing ? (
                  <textarea className="w-full min-h-[100px] p-4 bg-white border border-[#D8DDE6] rounded text-[13px] text-[#080707] resize-none" value={formData.notes || ''} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Add notes about this deal…" />
                ) : (
                  <div className="p-4 bg-[#F4F6F9] rounded border border-[#D8DDE6] text-[13px] text-[#080707] min-h-[100px] shadow-inner font-medium">
                    {deal.notes || 'No notes recorded for this deal.'}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'activities' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              {activities.length > 0 ? activities.slice(0, 20).map(a => (
                <div key={a._id} className="sf-card p-4 flex gap-4 hover:border-[#0176D3] transition-all">
                  <div className="w-10 h-10 rounded border border-[#D8DDE6] bg-[#F4F6F9] flex items-center justify-center shrink-0">
                    <Activity size={18} className="text-[#0176D3]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-[14px] font-bold text-[#080707]">{a.title}</h4>
                      <span className="text-[11px] font-bold text-[#706E6B]">{formatDate(a.createdAt)}</span>
                    </div>
                    <p className="text-[12px] text-[#706E6B] line-clamp-2">{a.description}</p>
                  </div>
                </div>
              )) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
                  <Activity size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No activity recorded</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              {tasks.length > 0 ? tasks.map(t => (
                <div
                  key={t._id}
                  onClick={() => navigate(`/dashboard/crm/tasks/${t._id}`)}
                  className="sf-card p-4 flex items-center gap-4 hover:border-[#0176D3] transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 rounded border border-[#D8DDE6] bg-[#F4F6F9] flex items-center justify-center shrink-0 text-[#2E844A]">
                    <CheckCircle size={18} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-[14px] font-bold text-[#080707] hover:text-[#0176D3]">{t.title}</h4>
                    <div className="flex items-center gap-2 text-[11px] font-bold text-[#706E6B]">
                      <Clock size={12} /> Due {t.dueDate ? formatDate(t.dueDate) : 'N/A'}
                    </div>
                  </div>
                  <span className={cn('sf-badge', t.priority === 'high' ? 'sf-badge-error' : 'sf-badge-neutral')}>{t.priority}</span>
                </div>
              )) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
                  <Clock size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No pending tasks</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <RelatedFiles module="deals" moduleId={dealId} recordName={deal?.title} />
            </div>
          )}

          {activeTab === 'formActivity' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <FormActivityPanel recordType="deal" recordId={dealId} />
            </div>
          )}

          {activeTab === 'finance' && <DealFinancePanel dealId={dealId} />}
          {activeTab === 'invoices' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              {invoices.length === 0 ? (
                <p className="text-[13px] text-[#706E6B] text-center py-6">
                  No invoices generated for this deal yet.
                </p>
              ) : (
                <div className="divide-y divide-[#D8DDE6]">
                  {invoices.map((inv) => (
                    <div
                      key={inv._id}
                      onClick={() => navigate(`/dashboard/crm/invoices?highlight=${inv._id}`)}
                      className="py-3 flex items-center justify-between cursor-pointer hover:bg-[#F4F6F9] px-2 -mx-2 rounded"
                    >
                      <div>
                        <p className="text-[13px] font-bold text-[#080707]">{inv.invoiceNumber}</p>
                        <p className="text-[11px] text-[#706E6B] uppercase tracking-wider">{inv.status}</p>
                      </div>
                      <p className="text-[13px] font-bold text-[#080707]">
                        {formatCurrency(inv.totalAmount, inv.currency)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <AuditHistory resourceType="deal" resourceId={dealId} />
          )}
        </div>

        {/* Sidebar Info */}
        <div className="lg:col-span-4 space-y-6">
          <div className="sf-card p-6 bg-white space-y-6">
            <h3 className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest border-b border-[#D8DDE6] pb-3 mb-4">Deal Status</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Status</span>
                <span className={cn('sf-badge', deal.status === 'won' ? 'sf-badge-success' : deal.status === 'lost' ? 'sf-badge-error' : 'sf-badge-neutral')}>
                  {(deal.status || 'open').toUpperCase()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Stage</span>
                <span className={cn('sf-badge', stageInfo.badge)}>{stageInfo.label}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Created</span>
                <span className="text-[12px] font-bold text-[#080707]">{formatDate(deal.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Record Owner</span>
                <span className="text-[12px] font-bold text-[#080707]">{deal.ownerName || 'Unassigned'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Created By</span>
                <span className="text-[12px] font-bold text-[#080707]">{deal.createdByName || '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Last Modified By</span>
                <span className="text-[12px] font-bold text-[#080707]">{deal.updatedByName || '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Modified On</span>
                <span className="text-[12px] font-bold text-[#080707]">{deal.updatedAt ? formatDate(deal.updatedAt) : '—'}</span>
              </div>
              {deal.wonReason && (
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-medium text-[#706E6B]">Won Reason</span>
                  <span className="text-[12px] font-bold text-[#080707]">{deal.wonReason}</span>
                </div>
              )}
              {deal.lostReason && (
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-medium text-[#706E6B]">Lost Reason</span>
                  <span className="text-[12px] font-bold text-[#080707]">{deal.lostReason}</span>
                </div>
              )}
              {deal.competitorName && (
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-medium text-[#706E6B]">Lost To</span>
                  <span className="text-[12px] font-bold text-[#080707]">{deal.competitorName}</span>
                </div>
              )}
            </div>
          </div>

          <div className="sf-card p-6 bg-white space-y-4">
            <h3 className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest border-b border-[#D8DDE6] pb-3 mb-2">Related Records</h3>
            {deal.leadId && (
              <div
                className="flex items-center gap-3 p-3 rounded border border-[#D8DDE6] hover:border-[#0176D3] cursor-pointer transition-colors"
                onClick={() => navigate(`/dashboard/crm/leads/${deal.leadId._id}`)}
              >
                <div className="w-8 h-8 rounded-full bg-[#0176D3] flex items-center justify-center text-white shrink-0"><User size={14} /></div>
                <div className="min-w-0">
                  <p className="text-[9px] font-bold text-[#706E6B] uppercase tracking-widest">Source Lead</p>
                  <p className="text-[13px] font-bold text-[#080707] truncate">{deal.leadId.name}</p>
                </div>
              </div>
            )}
            {deal.crmContactId && (
              <div
                className="flex items-center gap-3 p-3 rounded border border-[#D8DDE6] hover:border-[#0176D3] cursor-pointer transition-colors"
                onClick={() => navigate(`/dashboard/crm/contacts/${deal.crmContactId._id}`)}
              >
                <div className="w-8 h-8 rounded-full bg-[#2E844A] flex items-center justify-center text-white shrink-0"><User size={14} /></div>
                <div className="min-w-0">
                  <p className="text-[9px] font-bold text-[#706E6B] uppercase tracking-widest">Contact</p>
                  <p className="text-[13px] font-bold text-[#080707] truncate">{deal.crmContactId.name}</p>
                </div>
              </div>
            )}
            {!deal.leadId && !deal.crmContactId && (
              <p className="text-[12px] text-[#706E6B] font-medium">No linked lead or contact.</p>
            )}
          </div>

          <div className="sf-card p-6 bg-[#F4F6F9] border border-dashed border-[#D8DDE6] space-y-2">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest flex items-center gap-2"><Hash size={14} className="text-[#0176D3]" /> Deal ID</p>
            <p className="text-[9px] text-[#706E6B] font-mono break-all">{deal._id}</p>
          </div>
        </div>
      </div>

      <ApplyTemplateModal
        isOpen={showApplyTemplate}
        onClose={() => setShowApplyTemplate(false)}
        dealId={deal._id}
      />
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceType="deal"
        resourceId={deal._id}
        resourceLabel="Deal"
      />
      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        resourceType="deal"
        resourceId={deal._id}
        currentOwnerId={deal.ownerId}
        onTransferred={() => fetchDealData()}
      />

      <DealOutcomeModal
        open={outcomeModal.open}
        mode={outcomeModal.mode}
        dealTitle={deal.title}
        onClose={closeOutcomeModal}
        onConfirm={handleOutcomeConfirm}
      />

      {dealBilling && (
        <BillingSliceModal
          title={`Invoice ${deal?.title || 'deal'}`}
          total={dealBilling.dealValue}
          billed={dealBilling.billed}
          currency={deal?.currency}
          submitting={creatingInvoice}
          onClose={() => setDealBilling(null)}
          onSubmit={submitDealInvoice}
        />
      )}
      <GenerateInvoiceModal
        isOpen={showGenerateInvoiceModal}
        onClose={() => setShowGenerateInvoiceModal(false)}
        dealId={deal._id}
        onCreated={(invoice) => {
          setInvoices((prev) => [invoice, ...prev]);
          setActiveTab('invoices');
        }}
      />
    </div>
  );
}
